import React from 'react';
import { Link } from 'react-router-dom';
import { companyInfo } from '../data/mock';
import { Facebook, Linkedin, Instagram, Mail, Phone, MapPin, Clock, Navigation } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Our Providers', path: '/providers' },
    { name: 'For Participants', path: '/participants' },
    { name: 'Join a Study', path: '/join-study' },
    { name: 'Contact Us', path: '/contact' }
  ];

  return (
    <footer className="bg-[#0a1628] text-white">
      {/* Quick Action Bar */}
      <div className="bg-[#1877F2] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white font-semibold text-lg">Ready to participate in clinical research?</p>
            <div className="flex flex-wrap items-center gap-3">
              <a 
                href={`tel:${companyInfo.contact.phone}`}
                className="flex items-center space-x-2 bg-white text-[#1877F2] px-5 py-2 rounded-full font-bold text-sm hover:bg-[#E6F4FA] transition-all"
                data-testid="footer-call-btn"
              >
                <Phone size={16} />
                <span>Call Now</span>
              </a>
              <a 
                href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.contact.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-white/20 text-white px-5 py-2 rounded-full font-bold text-sm hover:bg-white/30 transition-all"
                data-testid="footer-directions-btn"
              >
                <Navigation size={16} />
                <span>Get Directions</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <img src={companyInfo.logo} alt={companyInfo.name} className="h-20 w-auto bg-white p-2 rounded-lg mb-4" />
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Leading Integrated Research Organization delivering innovative clinical trials for the pharmaceutical industry.
            </p>
            <div className="flex space-x-3">
              {[
                { icon: Facebook, href: companyInfo.socialLinks.facebook, name: 'facebook' },
                { icon: Linkedin, href: companyInfo.socialLinks.linkedin, name: 'linkedin' },
                { icon: Instagram, href: companyInfo.socialLinks.instagram, name: 'instagram' }
              ].map((social) => (
                <a key={social.name} href={social.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#1877F2] rounded-full flex items-center justify-center hover:bg-[#87CEEB] transition-colors">
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-[#87CEEB] text-sm transition-colors flex items-center"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="w-1.5 h-1.5 bg-[#1877F2] rounded-full mr-2"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Contact Us</h3>
            <div className="space-y-4">
              <a href={`tel:${companyInfo.contact.phone}`} className="flex items-start space-x-3 text-gray-400 hover:text-[#87CEEB] transition-colors group">
                <div className="w-10 h-10 bg-[#1877F2]/20 rounded-lg flex items-center justify-center group-hover:bg-[#1877F2] transition-colors flex-shrink-0">
                  <Phone size={18} className="text-[#1877F2] group-hover:text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p className="text-sm font-medium">{companyInfo.contact.phone}</p>
                </div>
              </a>
              <a href={`mailto:${companyInfo.contact.email}`} className="flex items-start space-x-3 text-gray-400 hover:text-[#87CEEB] transition-colors group">
                <div className="w-10 h-10 bg-[#1877F2]/20 rounded-lg flex items-center justify-center group-hover:bg-[#1877F2] transition-colors flex-shrink-0">
                  <Mail size={18} className="text-[#1877F2] group-hover:text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p className="text-sm font-medium break-all">{companyInfo.contact.email}</p>
                </div>
              </a>
              <a href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.contact.address)}`} target="_blank" rel="noopener noreferrer" className="flex items-start space-x-3 text-gray-400 hover:text-[#87CEEB] transition-colors group">
                <div className="w-10 h-10 bg-[#1877F2]/20 rounded-lg flex items-center justify-center group-hover:bg-[#1877F2] transition-colors flex-shrink-0">
                  <MapPin size={18} className="text-[#1877F2] group-hover:text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Address</p>
                  <p className="text-sm font-medium">{companyInfo.contact.address}</p>
                </div>
              </a>
            </div>
          </div>

          {/* Hours & CTA */}
          <div>
            <h3 className="font-bold text-lg mb-4 text-white">Business Hours</h3>
            <div className="bg-[#1877F2]/10 rounded-xl p-4 mb-4">
              <div className="flex items-center space-x-2 mb-3">
                <Clock size={18} className="text-[#87CEEB]" />
                <span className="font-semibold text-white">Office Hours</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Monday - Friday</span>
                  <span className="text-white font-medium">9:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Saturday - Sunday</span>
                  <span className="text-gray-500">Closed</span>
                </div>
              </div>
            </div>
            <Link 
              to="/join-study"
              className="block w-full bg-[#1877F2] text-white text-center py-3 rounded-full font-semibold hover:bg-[#166FE5] transition-colors"
              data-testid="footer-join-study-btn"
            >
              Join a Study Today
            </Link>
          </div>
        </div>
      </div>

      {/* Reference Links */}
      <div className="border-t border-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-400 text-xs mb-4">Clinical Trial Resources</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a 
              href="https://clinicaltrials.gov/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#1877F2]/20 hover:bg-[#1877F2]/40 text-[#87CEEB] px-4 py-2 rounded-lg text-xs font-medium transition-all border border-[#1877F2]/30"
              data-testid="ref-clinicaltrials-gov"
            >
              ClinicalTrials.gov
            </a>
            <a 
              href="https://www.nih.gov/health-information/nih-clinical-research-trials-you/finding-clinical-trial" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#1877F2]/20 hover:bg-[#1877F2]/40 text-[#87CEEB] px-4 py-2 rounded-lg text-xs font-medium transition-all border border-[#1877F2]/30"
              data-testid="ref-nih-gov"
            >
              NIH Clinical Trials
            </a>
            <a 
              href="https://www.centerwatch.com/clinical-trials/listings" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-[#1877F2]/20 hover:bg-[#1877F2]/40 text-[#87CEEB] px-4 py-2 rounded-lg text-xs font-medium transition-all border border-[#1877F2]/30"
              data-testid="ref-centerwatch"
            >
              CenterWatch
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500 gap-2">
            <span>© {new Date().getFullYear()} {companyInfo.fullName}. All rights reserved.</span>
            <span>Website Designer: <a href="https://obeinsonenterprise.com" target="_blank" rel="noopener noreferrer" className="text-[#87CEEB] font-medium hover:text-white transition-colors">OBEINSON ENTERPRISE LLC</a></span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
